/**
 * The single 0-5 intensity scale used wherever a trait is rated by a number: the style
 * profile ranges, the guideline ranges on the style page and the scoresheet sliders.
 *
 *   0 None, 1 Low, 2 Medium-low, 3 Medium, 4 Medium-high, 5 High
 *
 * The same digit has to read the same everywhere, so screens take the words from here
 * instead of keeping their own list. The backend keeps the same scale in
 * `Breward.Core.Styles.Guidelines.IntensityScale`.
 */

export const INTENSITY_MIN = 0;
export const INTENSITY_MAX = 5;

export type IntensityKey =
  | 'none'
  | 'low'
  | 'mediumLow'
  | 'medium'
  | 'mediumHigh'
  | 'high';

interface IntensityLevel {
  key: IntensityKey;
  en: string;
  pl: string;
}

const LEVELS: readonly IntensityLevel[] = [
  { key: 'none', en: 'None', pl: 'Brak' },
  { key: 'low', en: 'Low', pl: 'Niski' },
  { key: 'mediumLow', en: 'Medium-low', pl: 'Średnio-niski' },
  { key: 'medium', en: 'Medium', pl: 'Średni' },
  { key: 'mediumHigh', en: 'Medium-high', pl: 'Średnio-wysoki' },
  { key: 'high', en: 'High', pl: 'Wysoki' },
];

type Translate = (key: string, fallback: string) => string;

/** Rounds a value to the nearest level of the scale; anything that is not a number is 0. */
export const toIntensityLevel = (value: unknown): number => {
  const number = Number(value);
  if (value === null || value === '' || !Number.isFinite(number)) return INTENSITY_MIN;
  return Math.min(INTENSITY_MAX, Math.max(INTENSITY_MIN, Math.round(number)));
};

/**
 * Translation key (in the `global` namespace) and default text of a level. The default
 * is Polish for a Polish `language`, English otherwise.
 */
export const intensityLabel = (
  level: unknown,
  language?: string,
): { key: string; fallback: string } => {
  const entry = LEVELS[toIntensityLevel(level)];
  return {
    key: `global:shared.intensity.${entry.key}`,
    fallback: language?.toLowerCase().startsWith('pl') ? entry.pl : entry.en,
  };
};

/** Translated name of a level, e.g. `Medium-low` / `Średnio-niski` for 2. */
export const intensityText = (t: Translate, level: unknown, language?: string): string => {
  const { key, fallback } = intensityLabel(level, language);
  return t(key, fallback);
};

/** Translated name of a range: one name for a single level, `Low – Medium` otherwise. */
export const intensityRangeText = (
  t: Translate,
  min: unknown,
  max: unknown,
  language?: string,
): string => {
  const [low, high] = [toIntensityLevel(min), toIntensityLevel(max)];
  return low === high
    ? intensityText(t, low, language)
    : `${intensityText(t, low, language)} – ${intensityText(t, high, language)}`;
};

/**
 * Picks the label of a slider value from the labels the scoresheet field carries. The
 * labels are one per step (`min`, `min + step`, ...), so the value's step is the index;
 * only when the field has a different number of labels than steps is the position
 * spread proportionally over them.
 */
export const sliderStepLabel = (
  labels: readonly string[] | undefined,
  value: number | null | undefined,
  { min = 0, max = 10, step = 1 }: { min?: number; max?: number; step?: number } = {},
): string | undefined => {
  if (!labels?.length || value === undefined || value === null || !Number.isFinite(value)) {
    return undefined;
  }

  const lastStep = Math.max(0, Math.round((max - min) / (step || 1)));
  const stepIndex = Math.min(lastStep, Math.max(0, Math.round((value - min) / (step || 1))));

  if (labels.length === lastStep + 1) return labels[stepIndex];

  return labels[Math.round((stepIndex / Math.max(lastStep, 1)) * (labels.length - 1))];
};

interface SliderSettings {
  labels?: unknown;
  translations?: Record<string, { labels?: unknown } | undefined> | null;
}

/** The labels of a slider field in the given language, falling back to the default ones. */
export const sliderLabels = (settings?: SliderSettings | null, language?: string): string[] => {
  const raw = settings?.translations?.[language ?? '']?.labels ?? settings?.labels;
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string' && raw) return raw.split(',');
  return [];
};

/**
 * Names of the ends of a range rule on a slider field, e.g. `Średnio-niski – Średni` for
 * 2..3. Uses the labels the field carries and, for a field without any on the 0-5 scale,
 * the intensity scale. `undefined` when there is nothing to name the levels with.
 */
export const sliderRangeText = (
  t: Translate,
  settings: (SliderSettings & { minValue?: number; maxValue?: number; step?: number }) | null | undefined,
  ruleMin: number,
  ruleMax: number,
  language?: string,
): string | undefined => {
  const scale = {
    min: settings?.minValue ?? 0,
    max: settings?.maxValue ?? INTENSITY_MAX,
    step: settings?.step ?? 1,
  };
  const labels = sliderLabels(settings, language);

  if (labels.length) {
    const [low, high] = [
      sliderStepLabel(labels, ruleMin, scale),
      sliderStepLabel(labels, ruleMax, scale),
    ];
    if (!low || !high) return undefined;
    return low === high ? low : `${low} – ${high}`;
  }

  if (scale.min === INTENSITY_MIN && scale.max === INTENSITY_MAX && scale.step === 1) {
    return intensityRangeText(t, ruleMin, ruleMax, language);
  }
  return undefined;
};
